import { useMemo, useState } from 'react';
import type { PhaseOneExtractionResponse } from '../types';

interface Props {
  extraction: PhaseOneExtractionResponse;
  onNext: () => void;
  onReupload?: () => void;
}

const ExtractionSummaryCard = ({ extraction, onNext, onReupload }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const { totalProducts, totalCategories, allCategories } = useMemo(() => {
    const cats = extraction?.menu?.menuData?.categories ?? [];
    const totalCats = cats.length;
    let totalProds = 0;
    for (const c of cats) totalProds += (c?.products ?? []).length;

    return { totalProducts: totalProds, totalCategories: totalCats, allCategories: cats };
  }, [extraction]);

  const categories = allCategories;

  // Build a preview limited to max 4 products across categories
  const previewBlocks = useMemo(() => {
    const blocks: Array<{ name: string; products: any[] }> = [];
    const MAX = 4;
    let added = 0;
    for (const c of categories) {
      if (added >= MAX) break;
      const remaining = MAX - added;
      const items = (c?.products ?? []).slice(0, remaining);
      if (items.length > 0) {
        blocks.push({ name: c.categoryName, products: items });
        added += items.length;
      }
    }
    return blocks;
  }, [categories]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 p-6 shadow-sm backdrop-blur">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Found {totalProducts} products across {totalCategories} categories
        </p>

        {/* Intentionally not showing event fields here; keep focus on extracted menu */}

        {/* Menu list - looks like full menu; when collapsed, it is clipped with a soft shadow */}
        <div className="relative rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className={(expanded ? 'max-h-none' : 'max-h-64 md:max-h-80 overflow-hidden') + ' relative'}>
            <div className="divide-y divide-slate-100">
              {(expanded ? categories : previewBlocks).map((cat: any, idx: number) => (
                <div key={idx} className="p-4">
                  <div className="mb-2 text-sm font-semibold text-slate-900">{cat?.categoryName ?? cat?.name ?? cat?.name}</div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {(cat?.products ?? cat?.items ?? cat?.Products ?? []).map((p: any, i: number) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2">
                        <div className="text-sm text-slate-900 truncate mr-2" title={p?.name ?? ''}>{p?.name ?? 'Unnamed'}</div>
                        <div className="text-sm tabular-nums text-slate-700">{p?.price != null ? `$${Number(p.price).toFixed(2)}` : '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!expanded && (
              // Single bottom fade only (neutral), to imply more content below
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>
          <div className="p-3 text-center">
            <button type="button" className="text-sm font-semibold text-slate-700 hover:text-slate-900" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : 'See full menu'}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-800">Looks good. Next, we’ll customize your analysis.</p>
          <div className="mt-3 flex items-center justify-end gap-3">
            <button type="button" className="btn-primary" onClick={onNext}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExtractionSummaryCard;
